import React from 'react';
import { Input } from '../../../../components/forms/Input';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { Toggle } from '../../../../components/forms/Toggle';
import { CategoryColor, Network } from '../../../../models/graphql';
import {
  GetCategories_workspace_categories,
  GetSocialProfiles_workspace_socialProfiles
} from '../../../../models/legacy-graphql';
import { CategoryColorPicker } from '../../../../components/forms/CategoryColorPicker';
import { SocialProfile } from '../../../../components/socialProfiles/SocialProfile';
import classes from './CreateEditCategoryModal.module.scss';
import { BoxNotification } from '../../../../components/containers/BoxNotification';
import { CalendarPicker } from '../../../../components/actions/CalendarPicker';
import { FormModal } from '../../../../components/containers/FormModal';
import { Loader } from '../../../../components/containers/Loader';
import { profilesHelpers } from '../../../Settings/Profiles/Profiles.helpers';
import { CheckboxList } from '../../../../components/forms/CheckboxList';
import { ValidationMessages } from '../../../../constants/validation-messages';
import { SelectAllType } from '../../../../components/forms/CheckboxList/CheckboxList.view';
import classNames from 'classnames';
import { InputTypes } from '../../../../constants/form';
import { useSettings } from 'hooks/useSettings';
import { Link } from 'react-router-dom';

export type CreateEditCategoryModalViewProps = {
  onClose: () => void;
  socialProfiles: GetSocialProfiles_workspace_socialProfiles[] | [];
  useFormInstance: UseFormReturn;
  onSubmit: (data: {
    name: string;
    color: CategoryColor;
    seasonal: boolean;
    profiles: number[];
    seasonalStartDate: string;
    seasonalEndDate: string;
  }) => void;
  submitted: boolean;
  nameRules: RegisterOptions;
  toggleRules: RegisterOptions;
  colorPickerRules: RegisterOptions;
  calendarRules: RegisterOptions;
  profileRules: RegisterOptions;
  category?: GetCategories_workspace_categories;
  showProfileSelector: boolean;
  toggleProfileHandler: (newVal: boolean, complete: (keepNewVal: boolean | undefined) => void) => void;
  showCalendarSelector: boolean;
  toggleCalendarHandler: (newVal: boolean, complete: (keepNewVal: boolean | undefined) => void) => void;
  checkAllProfiles: boolean;
  setCheckAllProfiles: (checked: boolean) => void;
  calendarStartDate: Date | undefined;
  calendarEndDate: Date | undefined;
  isLoading: boolean;
};

export const CreateEditCategoryModalView = (props: CreateEditCategoryModalViewProps): JSX.Element => {
  const modalTitle = props.category?.id ? 'Update Category' : 'Add Category';
  const d = new Date();
  const range = `${d.getFullYear()}`;
  const { selectedOrgSettings } = useSettings();
  let ListOfProfiles = props.socialProfiles.flatMap(
    (profile: GetSocialProfiles_workspace_socialProfiles, index: number): JSX.Element[] | JSX.Element => {
      const network: Network | null = profilesHelpers.mapNetworkFromTypename(profile.__typename);

      const wrapperClass = classNames(
        index !== props.socialProfiles.length - 1 ? 'margin-bottom-small-8' : 'margin-bottom-small-16',
        {
          'margin-top-small-8': index > 0
        }
      );

      if (!network) {
        return [];
      }

      return (
        <div key={`profile-key-${profile?.id}`} className={wrapperClass}>
          <SocialProfile
            checkbox={true}
            size={'large'}
            label={true}
            sublabel={true}
            socialProfile={profilesHelpers.remapSocialProfile(profile, false, false, false)}
            disconnected={!profile.connected}
            disabled={props.submitted}
            loading={props.submitted}
            rules={
              props.socialProfiles.length === 1
                ? { required: ValidationMessages.categoriesSocialProfiles.required }
                : {}
            }
            useFormInstance={props.useFormInstance}
            image={profile.photoUrl ? profile.photoUrl : undefined}
            useFormName={`profiles.${index}`}
            value={profile.id}
          />
        </div>
      );
    }
  );

  ListOfProfiles = ListOfProfiles.filter(p => p != null);

  return (
    <FormModal
      showCloseIcon={!props.submitted}
      formInstance={props.useFormInstance}
      loading={props.submitted}
      onClose={props.onClose}
      onSubmit={props.onSubmit}
      size={'small'}
      submitText={'Save Category'}
      title={modalTitle}>
      <div className={`padding-bottom-small-20`}>
        <Input
          disabled={props.submitted}
          formInstance={props.useFormInstance}
          name={'name'}
          type={InputTypes.text}
          size={'medium'}
          label={`Category name`}
          rules={props.nameRules}
        />
      </div>
      <div className={`padding-bottom-small-20`}>
        <CategoryColorPicker
          label={'Category colour'}
          name={'color'}
          rules={props.colorPickerRules}
          useFormInstance={props.useFormInstance}
          disabled={props.submitted}
        />
      </div>
      <div className={'grid-x align-justify margin-bottom-small-24'}>
        <Toggle
          formInstance={props.useFormInstance}
          rules={props.toggleRules}
          name={'seasonal'}
          label={'Seasonal Category'}
          helper={''}
          action={(show, complete) => {
            props.toggleCalendarHandler(!props.showCalendarSelector, complete);
          }}
          disabled={props.submitted}
          checked={props.category?.seasonal}
        />
        {props.showCalendarSelector && (
          <div className={'grid-x align-justify margin-top-small-16'}>
            <div className={`margin-bottom-small-16 cell ${classes.CalendarPickerContainer}`}>
              <CalendarPicker
                label={'Active from'}
                name={'seasonalStartDate'}
                useFormInstance={props.useFormInstance}
                inputSize={'large'}
                rules={props.calendarRules}
                date={props.calendarStartDate}
                disabled={props.submitted}
                hideYear
                calendarProps={{
                  dateFormat: 'M dd',
                  yearRange: `"${range}:${range}"`
                }}
                startDay={selectedOrgSettings?.startOfWeekFormat}
              />
            </div>
            <div className={`cell shrink ${classes.DashContainer}`}>
              <div className={classes.Dash}>
                <span className={'show-for-sr'}>-</span>
              </div>
            </div>
            <div className={`margin-bottom-small-16 cell ${classes.CalendarPickerContainer}`}>
              <CalendarPicker
                name={'seasonalEndDate'}
                label={'Active to'}
                useFormInstance={props.useFormInstance}
                hideYear
                inputSize={'large'}
                rules={props.calendarRules}
                date={props.calendarEndDate}
                disabled={props.submitted}
                calendarProps={{
                  dateFormat: 'M dd'
                }}
                startDay={selectedOrgSettings?.startOfWeekFormat}
              />
            </div>
          </div>
        )}
      </div>
      <div className={'grid-x align-justify margin-bottom-small-24'}>
        <Toggle
          rules={props.toggleRules}
          formInstance={props.useFormInstance}
          name={'profilesToggle'}
          label={'Profiles selected by default'}
          helper={''}
          disabled={props.submitted}
          action={(show, complete) => {
            props.toggleProfileHandler(!props.showProfileSelector, complete);
          }}
          checked={props.showProfileSelector}
        />
      </div>
      {props.showProfileSelector && (
        <div className={'margin-bottom-small-16'}>
          {props.isLoading ? (
            <Loader variation={'dark'} size={'small'} />
          ) : (
            <>
              {!props.socialProfiles.length || !ListOfProfiles.length ? (
                <BoxNotification type={'error'}>
                  There are no Profiles in this Workspace.
                  <br />
                  <Link to={'/settings/profiles'} target={'_blank'}>
                    Connect a Profile
                  </Link>{' '}
                  to get started.
                </BoxNotification>
              ) : (
                <>
                  <div className={classes.Label}>
                    These Profiles will be pre-selected when creating a new Post for this Category in the Post Editor
                  </div>
                  <div className={`margin-top-small-8`}>
                    <CheckboxList
                      {...((ListOfProfiles.length > 1
                        ? {
                            selectAll: true,
                            checkboxesChildrenGroupName: 'profiles',
                            selectAllSize: 'small',
                            selectAllValidation: { required: ValidationMessages.socialProfiles.categories.required }
                          }
                        : {
                            selectAll: false
                          }) as SelectAllType)}
                      label={''}
                      formInstance={props.useFormInstance}
                      disabled={props.submitted}>
                      {ListOfProfiles}
                    </CheckboxList>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </FormModal>
  );
};
